import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, XIcon } from 'lucide-react';
import { useAuth } from '../hooks';
import { Alert, MockdLogo } from '../components/common';

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resetPassword, loading, error } = useAuth();

  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    try {
      await resetPassword(token!, password);
      navigate('/dashboard', { replace: true });
    } catch {
      // Error is handled by useAuth
    }
  };

  if (!token) {
    return (
      <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="card bg-base-100 p-6">
            <div className="w-12 h-12 mx-auto mb-4 bg-error/20 rounded-full flex items-center justify-center">
              <XIcon className="size-6 text-error" />
            </div>
            <h1 className="text-base-content mb-2">Invalid Link</h1>
            <p className="text-base-content/80 mb-6">This password reset link is invalid.</p>
            <Link to="/forgot-password" className="btn btn-primary w-full">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <MockdLogo size="md" className="mx-auto" />
          <h1 className="mt-4 text-base-content">Choose a new password</h1>
        </div>

        <div className="p-6">
          {(localError || error) && (
            <Alert variant="error" className="mb-4">{localError || error}</Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="fieldset">
              <label htmlFor="password" className="fieldset-legend">
                New password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="input w-full pr-10"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/70 hover:text-base-content"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOffIcon className="size-5" />
                  ) : (
                    <EyeIcon className="size-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="fieldset">
              <label htmlFor="confirmPassword" className="fieldset-legend">
                Confirm new password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="input w-full"
                placeholder="Repeat your new password"
              />
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? 'Resetting...' : 'Reset password'}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 text-center text-sm">
              <Link to="/forgot-password" className="link link-primary">
                Request a new link
              </Link>
            </div>
          )}
        </div>

        <div className="mt-4 text-center">
          <Link to="/login" className="link link-hover text-sm text-base-content/80">
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
